/**
 * Recursively parse a single string value, unwrapping nested JSON layers.
 * Stops when the result is a non-string or when it can't be parsed further.
 */
export function parseString(value: string): unknown {
  const trimmed = value.trim();
  const looksLikeJson =
    trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"');
  if (!looksLikeJson) return value;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    // If we got another string, keep unwrapping
    if (typeof parsed === 'string') return parseString(parsed);
    // Otherwise walk the result for nested JSON strings
    return deepWalk(parsed);
  } catch {
    return value;
  }
}

/**
 * Walk an object/array tree and recursively parse any string values
 * that look like JSON, in-place. Returns a new deep copy.
 */
export function deepWalk(value: unknown): unknown {
  if (typeof value === 'string') return parseString(value);
  if (Array.isArray(value)) return value.map(deepWalk);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]): [string, unknown] => [
        k,
        deepWalk(v),
      ]),
    );
  }
  return value;
}

/**
 * Entry point: try to deeply parse a payload.
 * - If it's already an object/array → walk it for nested JSON strings.
 * - If it's a string → unwrap + walk.
 * - canBeautified: true when we actually transformed anything.
 */
export function tryParseDeep(payload: unknown): { parsed: unknown; canBeautified: boolean } {
  if (payload === null || payload === undefined) return { parsed: payload, canBeautified: false };

  if (typeof payload === 'string') {
    const parsed = parseString(payload);
    // If it changed (we parsed something) it's beautifiable
    return { parsed, canBeautified: parsed !== payload };
  }

  // object/array — walk for nested JSON strings
  const walked = deepWalk(payload);
  // It's always beautifiable when it's an object (pretty-print is useful)
  return { parsed: walked, canBeautified: true };
}
